/**
 * BroadcastInfoEditor — the words, the slides and the giving details.
 *
 * The same screen for a church service, a conference session and a lesson,
 * because a media team should not learn three. It sits beside the credentials
 * and health panels on whichever admin screen owns the broadcast.
 *
 * Two things are deliberately not the same:
 *
 *  * Giving defaults to the church's existing setup rather than asking for a
 *    mobile money number again. If the church corrects its number on the
 *    giving page, every broadcast corrects itself. Only a conference with its
 *    own account switches to its own details.
 *  * The slide control saves on its own, instantly. A teacher pressing Next
 *    mid-sentence cannot be made to scroll down and press Save.
 */
import React, { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Loader2, Plus, Trash2, Save, ChevronLeft, ChevronRight, Upload, Info,
  Presentation, HandHeart, ListOrdered,
} from 'lucide-react';
import { toast } from 'sonner';

import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import api, { getErrorMessage } from '@/lib/api';

const MODES = [
  ['inherit', 'Use the church’s giving details'],
  ['custom', 'This broadcast has its own'],
  ['off', 'Do not show giving'],
];

const QR_MODES = [
  ['giving_page', 'Opens the giving page'],
  ['upload', 'A code I upload'],
  ['none', 'No QR code'],
];

const blank = {
  speaker: '', title: '', series: '', scripture: '', notices: '',
  order: [], slides: { enabled: false, current: 0, images: [] },
  giving: {
    mode: 'inherit', heading: '', message: '', give_url: '',
    qr_mode: 'giving_page', qr_image_url: '', methods: [],
    bank: { bank_name: '', account_name: '', account_number: '', branch: '', swift: '' },
  },
};

const Section = ({ icon: Icon, title, hint, children }) => (
  <div className="space-y-3 border-t border-slate-100 pt-4 first:border-0 first:pt-0">
    <div>
      <p className="flex items-center gap-1.5 text-sm font-semibold text-slate-900">
        <Icon className="h-4 w-4 text-slate-400" />{title}
      </p>
      {hint ? <p className="mt-0.5 text-xs text-slate-500">{hint}</p> : null}
    </div>
    {children}
  </div>
);

const Choice = ({ value, options, onChange, name }) => (
  <div className="flex flex-wrap gap-2">
    {options.map(([id, label]) => (
      <button
        key={id}
        type="button"
        onClick={() => onChange(id)}
        className={`rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${
          value === id
            ? 'border-emerald-600 bg-emerald-50 text-emerald-800'
            : 'border-slate-200 text-slate-600 hover:border-slate-300'}`}
        data-testid={`${name}-${id}`}
      >
        {label}
      </button>
    ))}
  </div>
);

export const BroadcastInfoEditor = ({ scope, ident, label = 'broadcast' }) => {
  const qc = useQueryClient();
  const [form, setForm] = useState(blank);
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState('');
  const fileRef = useRef(null);
  const qrRef = useRef(null);

  const { data, isLoading } = useQuery({
    queryKey: ['broadcast-info-admin', scope, ident],
    queryFn: () => api.get(`/live/${scope}/${ident}/info`).then((r) => r.data),
    enabled: !!scope && !!ident,
  });

  useEffect(() => {
    if (!data || dirty) return;
    // `giving.stored` is what was saved; the rest of `giving` is what viewers
    // are shown after inheritance. Editing the resolved values would write the
    // church's details back as this broadcast's own and lose the choice — a QR
    // code switched off for one service stayed off for every later one.
    const saved = data.giving?.stored || {};
    setForm({
      ...blank,
      ...['speaker', 'title', 'series', 'scripture', 'notices'].reduce(
        (a, k) => ({ ...a, [k]: data[k] || '' }), {}),
      order: data.order || [],
      slides: { ...blank.slides, ...(data.slides || {}) },
      giving: {
        ...blank.giving,
        ...saved,
        mode: saved.mode || 'inherit',
        qr_mode: saved.qr_mode || 'giving_page',
        methods: saved.methods || [],
        bank: { ...blank.giving.bank, ...(saved.bank || {}) },
      },
    });
  }, [data, dirty]);

  const set = (patch) => { setForm((f) => ({ ...f, ...patch })); setDirty(true); };
  const setGiving = (patch) => set({ giving: { ...form.giving, ...patch } });

  const save = useMutation({
    mutationFn: () => api.put(`/live/${scope}/${ident}/info`, form),
    onSuccess: () => {
      toast.success('Saved — this is what viewers now see');
      setDirty(false);
      qc.invalidateQueries({ queryKey: ['broadcast-info-admin', scope, ident] });
      qc.invalidateQueries({ queryKey: ['broadcast-info', scope, ident] });
    },
    onError: (e) => toast.error(getErrorMessage(e, 'Could not save')),
  });

  const upload = async (files, onDone) => {
    const list = Array.from(files || []);
    if (!list.length) return;
    setBusy('upload');
    try {
      const urls = [];
      for (const file of list) {
        const body = new FormData();
        body.append('file', file);
        body.append('purpose', 'photo');
        const r = await api.post('/media/upload', body);
        if (r.data?.url) urls.push({ url: r.data.url, caption: '' });
      }
      onDone(urls);
      if (urls.length) toast.success(`${urls.length} image${urls.length > 1 ? 's' : ''} added`);
    } catch (e) {
      toast.error(getErrorMessage(e, 'Upload failed'));
    } finally {
      setBusy('');
    }
  };

  const jump = useMutation({
    mutationFn: (current) => api.post(`/live/${scope}/${ident}/info/slide`, { current }),
    onSuccess: (r) => {
      setForm((f) => ({ ...f, slides: { ...f.slides, current: r.data.current } }));
      qc.invalidateQueries({ queryKey: ['broadcast-info', scope, ident] });
    },
    onError: (e) => toast.error(getErrorMessage(e, 'Could not change the slide')),
  });

  if (isLoading) {
    return (
      <p className="flex items-center gap-2 text-sm text-slate-500">
        <Loader2 className="h-4 w-4 animate-spin" />Loading broadcast information…
      </p>
    );
  }

  const images = form.slides.images || [];
  const inherited = data?.giving?.source === 'church';

  return (
    <Card data-testid={`broadcast-info-editor-${scope}`}>
      <CardContent className="space-y-5 p-4">
        <div>
          <h3 className="text-base font-semibold text-slate-900 md:text-lg">
            What viewers see beside the video
          </h3>
          <p className="mt-1 text-sm text-slate-500">
            The reading, the notices and how to give, on the public {label} page.
            Anything left blank simply does not appear.
          </p>
        </div>

        {images.length ? (
          <div className="flex flex-wrap items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 p-2.5"
               data-testid="slide-control">
            <span className="text-xs font-semibold text-emerald-900">
              Showing slide {(form.slides.current || 0) + 1} of {images.length}
            </span>
            <div className="ml-auto flex gap-1.5">
              <Button size="sm" variant="outline" data-testid="slide-prev"
                      disabled={jump.isPending || (form.slides.current || 0) <= 0}
                      onClick={() => jump.mutate((form.slides.current || 0) - 1)}>
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button size="sm" variant="outline" data-testid="slide-next"
                      disabled={jump.isPending || (form.slides.current || 0) >= images.length - 1}
                      onClick={() => jump.mutate((form.slides.current || 0) + 1)}>
                Next <ChevronRight className="ml-1 h-4 w-4" />
              </Button>
            </div>
            <p className="w-full text-[11px] text-emerald-800">
              Changes the slide for everybody watching straight away — no need to save.
            </p>
          </div>
        ) : null}

        <Section icon={Info} title="This broadcast">
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <Label className="text-xs">Sermon or session title</Label>
              <Input value={form.title} data-testid="info-title"
                     onChange={(e) => set({ title: e.target.value })}
                     placeholder="The God Who Keeps His Word" />
            </div>
            <div>
              <Label className="text-xs">Speaker</Label>
              <Input value={form.speaker} data-testid="info-speaker"
                     onChange={(e) => set({ speaker: e.target.value })}
                     placeholder="Pastor John Moyo" />
            </div>
            <div>
              <Label className="text-xs">Series</Label>
              <Input value={form.series} data-testid="info-series"
                     onChange={(e) => set({ series: e.target.value })}
                     placeholder="Faith That Works · Part 3" />
            </div>
            <div>
              <Label className="text-xs">Scripture reading</Label>
              <Input value={form.scripture} data-testid="info-scripture"
                     onChange={(e) => set({ scripture: e.target.value })}
                     placeholder="James 2:14–26" />
            </div>
          </div>
          <div>
            <Label className="text-xs">Notices and announcements</Label>
            <Textarea rows={3} value={form.notices} data-testid="info-notices"
                      onChange={(e) => set({ notices: e.target.value })}
                      placeholder="Can be changed while the service is on air." />
          </div>
        </Section>

        <Section icon={ListOrdered} title="Order of service"
                 hint="What comes next, so somebody joining late knows where we are.">
          <div className="space-y-2">
            {(form.order || []).map((o, i) => (
              <div key={i} className="flex gap-2">
                <Input className="w-24" value={o.time} placeholder="10:00"
                       data-testid={`order-time-${i}`}
                       onChange={(e) => {
                         const order = [...form.order];
                         order[i] = { ...o, time: e.target.value };
                         set({ order });
                       }} />
                <Input value={o.item} placeholder="Worship" data-testid={`order-item-${i}`}
                       onChange={(e) => {
                         const order = [...form.order];
                         order[i] = { ...o, item: e.target.value };
                         set({ order });
                       }} />
                <Button variant="ghost" size="icon" data-testid={`order-remove-${i}`}
                        onClick={() => set({ order: form.order.filter((_, j) => j !== i) })}>
                  <Trash2 className="h-4 w-4 text-rose-600" />
                </Button>
              </div>
            ))}
            <Button variant="outline" size="sm" data-testid="order-add"
                    onClick={() => set({ order: [...(form.order || []), { time: '', item: '' }] })}>
              <Plus className="mr-1 h-3.5 w-3.5" />Add an item
            </Button>
          </div>
        </Section>

        <Section icon={Presentation} title="Slides"
                 hint="For teaching: viewers see the slide you are on, beside the video.">
          <div className="flex items-center gap-2">
            <Switch checked={!!form.slides.enabled} data-testid="slides-enabled"
                    onCheckedChange={(v) => set({ slides: { ...form.slides, enabled: v } })} />
            <span className="text-sm text-slate-700">Show slides to viewers</span>
          </div>
          {form.slides.enabled ? (
            <>
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                {images.map((s, i) => (
                  <div key={i} className={`rounded-lg border p-1.5 ${
                    i === (form.slides.current || 0) ? 'border-emerald-500' : 'border-slate-200'}`}>
                    <img src={s.url} alt="" className="h-20 w-full rounded object-cover" />
                    <Input className="mt-1 h-7 text-[11px]" placeholder="Caption" value={s.caption}
                           data-testid={`slide-caption-${i}`}
                           onChange={(e) => {
                             const list = [...images];
                             list[i] = { ...s, caption: e.target.value };
                             set({ slides: { ...form.slides, images: list } });
                           }} />
                    <Button variant="ghost" size="sm" className="mt-1 h-6 w-full text-[11px] text-rose-600"
                            data-testid={`slide-remove-${i}`}
                            onClick={() => set({
                              slides: {
                                ...form.slides,
                                images: images.filter((_, j) => j !== i),
                                current: 0,
                              },
                            })}>
                      Remove
                    </Button>
                  </div>
                ))}
              </div>
              <input ref={fileRef} type="file" accept="image/*" multiple className="hidden"
                     onChange={(e) => upload(e.target.files, (urls) => set({
                       slides: { ...form.slides, images: [...images, ...urls] },
                     }))} />
              <Button variant="outline" size="sm" disabled={busy === 'upload'}
                      onClick={() => fileRef.current?.click()} data-testid="slides-upload">
                {busy === 'upload'
                  ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
                  : <Upload className="mr-1 h-3.5 w-3.5" />}
                Add slide images
              </Button>
            </>
          ) : null}
        </Section>

        <Section icon={HandHeart} title="Giving"
                 hint="A QR code for anybody watching on a laptop or television, and tap-to-copy numbers for anybody watching on a phone.">
          <Choice name="giving-mode" value={form.giving.mode} options={MODES}
                  onChange={(mode) => setGiving({ mode })} />

          {form.giving.mode === 'inherit' ? (
            inherited ? (
              <p className="rounded-lg border border-slate-200 bg-slate-50 p-2.5 text-xs text-slate-600"
                 data-testid="giving-inherited">
                Using {data.giving.source_name}’s giving details
                {data.giving.methods?.length
                  ? ` — ${data.giving.methods.map((m) => m.label).filter(Boolean).join(', ')}`
                  : ''}. Correct them on the giving page and every broadcast follows.
              </p>
            ) : (
              <p className="rounded-lg border border-amber-200 bg-amber-50 p-2.5 text-xs text-amber-900"
                 data-testid="giving-no-source">
                No church giving details were found for this {label}. Choose
                “This broadcast has its own” and type them in, or leave giving off.
              </p>
            )
          ) : null}

          {form.giving.mode !== 'off' ? (
            <div className="space-y-3">
              <div className="grid gap-3 sm:grid-cols-2">
                <div>
                  <Label className="text-xs">Heading</Label>
                  <Input value={form.giving.heading} data-testid="giving-heading"
                         onChange={(e) => setGiving({ heading: e.target.value })}
                         placeholder="Give to this ministry" />
                </div>
                {form.giving.mode === 'custom' ? (
                  <div>
                    <Label className="text-xs">Giving page link</Label>
                    <Input value={form.giving.give_url} data-testid="giving-url"
                           onChange={(e) => setGiving({ give_url: e.target.value })}
                           placeholder="https://…" />
                  </div>
                ) : null}
              </div>
              <div>
                <Label className="text-xs">Message to viewers</Label>
                <Textarea rows={2} value={form.giving.message} data-testid="giving-message"
                          onChange={(e) => setGiving({ message: e.target.value })}
                          placeholder="Dial the number below, or scan to give by card." />
              </div>

              <div>
                <Label className="text-xs">QR code</Label>
                <Choice name="giving-qr" value={form.giving.qr_mode} options={QR_MODES}
                        onChange={(qr_mode) => setGiving({ qr_mode })} />
                {form.giving.qr_mode === 'upload' ? (
                  <div className="mt-2 flex items-center gap-2">
                    {form.giving.qr_image_url ? (
                      <img src={form.giving.qr_image_url} alt="QR"
                           className="h-16 w-16 rounded border border-slate-200 object-contain" />
                    ) : null}
                    <input ref={qrRef} type="file" accept="image/*" className="hidden"
                           onChange={(e) => upload(e.target.files,
                             (urls) => urls[0] && setGiving({ qr_image_url: urls[0].url }))} />
                    <Button variant="outline" size="sm" disabled={busy === 'upload'}
                            onClick={() => qrRef.current?.click()} data-testid="giving-qr-upload">
                      <Upload className="mr-1 h-3.5 w-3.5" />
                      {form.giving.qr_image_url ? 'Replace code' : 'Upload your code'}
                    </Button>
                  </div>
                ) : null}
              </div>

              {form.giving.mode === 'custom' ? (
                <>
                  <div className="space-y-2">
                    <Label className="text-xs">Mobile money and other numbers</Label>
                    {(form.giving.methods || []).map((m, i) => (
                      <div key={i} className="flex flex-wrap gap-2">
                        <Input className="w-36" placeholder="EcoCash" value={m.label}
                               data-testid={`giving-method-label-${i}`}
                               onChange={(e) => {
                                 const methods = [...form.giving.methods];
                                 methods[i] = { ...m, label: e.target.value };
                                 setGiving({ methods });
                               }} />
                        <Input className="w-44" placeholder="077 123 456" value={m.number}
                               data-testid={`giving-method-number-${i}`}
                               onChange={(e) => {
                                 const methods = [...form.giving.methods];
                                 methods[i] = { ...m, number: e.target.value };
                                 setGiving({ methods });
                               }} />
                        <Input className="min-w-[10rem] flex-1" placeholder="How to send to it"
                               value={m.instructions} data-testid={`giving-method-note-${i}`}
                               onChange={(e) => {
                                 const methods = [...form.giving.methods];
                                 methods[i] = { ...m, instructions: e.target.value };
                                 setGiving({ methods });
                               }} />
                        <Button variant="ghost" size="icon" data-testid={`giving-method-remove-${i}`}
                                onClick={() => setGiving({
                                  methods: form.giving.methods.filter((_, j) => j !== i) })}>
                          <Trash2 className="h-4 w-4 text-rose-600" />
                        </Button>
                      </div>
                    ))}
                    <Button variant="outline" size="sm" data-testid="giving-method-add"
                            onClick={() => setGiving({
                              methods: [...(form.giving.methods || []),
                                        { label: '', number: '', instructions: '' }] })}>
                      <Plus className="mr-1 h-3.5 w-3.5" />Add a number
                    </Button>
                  </div>

                  <div className="grid gap-2 sm:grid-cols-2">
                    {[['bank_name', 'Bank'], ['account_name', 'Account name'],
                      ['account_number', 'Account number'], ['branch', 'Branch'],
                      ['swift', 'SWIFT / BIC']].map(([key, lbl]) => (
                      <div key={key}>
                        <Label className="text-xs">{lbl}</Label>
                        <Input value={form.giving.bank[key] || ''} data-testid={`giving-bank-${key}`}
                               onChange={(e) => setGiving({
                                 bank: { ...form.giving.bank, [key]: e.target.value } })} />
                      </div>
                    ))}
                  </div>
                </>
              ) : null}
            </div>
          ) : null}
        </Section>

        <div className="flex items-center gap-3 border-t border-slate-100 pt-4">
          <Button onClick={() => save.mutate()} disabled={save.isPending || !dirty}
                  data-testid="broadcast-info-save">
            {save.isPending
              ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
              : <Save className="mr-1.5 h-4 w-4" />}
            Save
          </Button>
          {dirty ? (
            <span className="text-xs text-amber-700" data-testid="broadcast-info-dirty">
              Not saved yet — viewers still see the previous version.
            </span>
          ) : null}
        </div>
      </CardContent>
    </Card>
  );
};

export default BroadcastInfoEditor;
